/**
 * Carte produit pour le catalogue
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  ArticleWithStock,
  reviewApi,
  StarRating,
  ArticleReviewStats,
  hasStock,
  ARTICLE_TYPE_LABELS,
} from '@e-store/shared';

interface ProductCardProps {
  article: ArticleWithStock;
  onAddToCart?: (articleId: string) => void;
}

export function ProductCard({ article, onAddToCart }: ProductCardProps) {
  const [reviewStats, setReviewStats] = useState<ArticleReviewStats | null>(null);

  useEffect(() => {
    loadReviewStats();
  }, [article.id]);

  const loadReviewStats = async () => {
    try {
      const response = await reviewApi.getArticleReviewStats(article.id);
      if (response.data) {
        setReviewStats(response.data);
      }
    } catch (err) {
      // Silently fail - stats are optional
    }
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('fr-MG', {
      style: 'decimal',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price) + ' Ar';
  };

  const mainImage = article.images && article.images.length > 0
    ? article.images[article.mainImageIndex || 0]
    : null;

  return (
    <div className="product-card">
      <Link to={`/product/${article.id}`} className="product-image-link">
        {mainImage ? (
          <img src={mainImage} alt={article.name} className="product-image" />
        ) : (
          <div className="product-image-placeholder">
            <span>Pas d'image</span>
          </div>
        )}
      </Link>

      <div className="product-info">
        <Link to={`/product/${article.id}`} className="product-name">
          {article.name}
        </Link>
        <p className="product-reference">{article.reference}</p>

        {/* Rating Stars */}
        {reviewStats && reviewStats.totalReviews > 0 && (
          <Link to={`/product/${article.id}#reviews`} className="product-rating-link">
            <StarRating
              rating={reviewStats.averageRating}
              totalReviews={reviewStats.totalReviews}
              size="small"
              showCount={true}
            />
          </Link>
        )}

        <p className="product-price">{formatPrice(article.priceHT)}</p>

        {hasStock(article.type) ? (
          <div className="product-stock">
            {article.isInStock ? (
              <span className="stock-available">Disponible</span>
            ) : (
              <span className="stock-unavailable">Non disponible</span>
            )}
          </div>
        ) : (
          /* Types sans stock (service, restaurant) : jamais en rupture */
          <div className="product-stock">
            <span className="stock-service">{ARTICLE_TYPE_LABELS[article.type]}</span>
          </div>
        )}

        <button
          onClick={() => onAddToCart?.(article.id)}
          className="btn btn-primary btn-add-to-cart"
          disabled={hasStock(article.type) && !article.isInStock}
        >
          Ajouter au panier
        </button>
      </div>
    </div>
  );
}
